import type {
	ChartData,
	ChartMetaInfo,
	ChartTemplate,
	ChartTemplateContent,
	EntityId,
	IExternalSaveLoadAdapter,
	LineToolState,
	LineToolsAndGroupsLoadRequestContext,
	LineToolsAndGroupsLoadRequestType,
	LineToolsAndGroupsState,
	StudyTemplateData,
	StudyTemplateMetaInfo,
} from "@public/charting_library";

const storageKeys = {
	charts: "trade-chart:adapter:charts:v1",
	studyTemplates: "trade-chart:adapter:study-templates:v1",
	drawingTemplates: "trade-chart:adapter:drawing-templates:v1",
	chartTemplates: "trade-chart:adapter:chart-templates:v1",
	drawings: "trade-chart:adapter:drawings:v1",
	lastChartId: "trade-chart:adapter:last-chart-id:v1",
} as const;

type DrawingTemplateRecord = {
	content: string;
	name: string;
	toolName: string;
};

type ChartTemplateRecord = {
	content: ChartTemplateContent;
	name: string;
};

type LayoutDrawings = Record<string, LineToolState>;
type SavedDrawings = Record<string, LayoutDrawings>;

function canUseStorage() {
	return typeof window !== "undefined";
}

function loadFromStorage<T>(key: string): T | null {
	if (!canUseStorage()) return null;

	try {
		const raw = window.localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : null;
	} catch (error) {
		console.warn(`[Chart] Failed to read adapter storage key "${key}".`, error);
		return null;
	}
}

function saveToStorage(key: string, value: unknown) {
	if (!canUseStorage()) return;

	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.warn(
			`[Chart] Failed to write adapter storage key "${key}".`,
			error,
		);
	}
}

function getDrawingKey(layoutId: string, chartId: string | number) {
	return `${layoutId}/${chartId}`;
}

let charts = loadFromStorage<ChartData[]>(storageKeys.charts) ?? [];
let studyTemplates =
	loadFromStorage<StudyTemplateData[]>(storageKeys.studyTemplates) ?? [];
let drawingTemplates =
	loadFromStorage<DrawingTemplateRecord[]>(storageKeys.drawingTemplates) ?? [];
let chartTemplates =
	loadFromStorage<ChartTemplateRecord[]>(storageKeys.chartTemplates) ?? [];
const drawings = loadFromStorage<SavedDrawings>(storageKeys.drawings) ?? {};
let isDirty = false;

if (typeof window !== "undefined") {
	window.setInterval(() => {
		if (!isDirty) {
			return;
		}

		saveToStorage(storageKeys.charts, charts);
		saveToStorage(storageKeys.studyTemplates, studyTemplates);
		saveToStorage(storageKeys.drawingTemplates, drawingTemplates);
		saveToStorage(storageKeys.chartTemplates, chartTemplates);
		saveToStorage(storageKeys.drawings, drawings);
		isDirty = false;
	}, 1000);
}

export function hasSavedChartsInAdapter() {
	return charts.length > 0;
}

export const LocalStorageSaveLoadAdapter: IExternalSaveLoadAdapter = {
	async getAllCharts() {
		return [...charts]
			.sort((left, right) => right.timestamp - left.timestamp)
			.map<ChartMetaInfo>((chart) => ({
				id: chart.id ?? "",
				name: chart.name,
				symbol: chart.symbol,
				resolution: chart.resolution,
				timestamp: chart.timestamp,
			}));
	},

	async removeChart(id) {
		charts = charts.filter((chart) => chart.id !== id);
		isDirty = true;
	},

	async saveChart(chartData) {
		const lastChartId = loadFromStorage<string | number>(
			storageKeys.lastChartId,
		);
		const chartId = chartData.id ?? lastChartId ?? crypto.randomUUID();

		charts = charts.filter((chart) => chart.id !== chartId);

		const savedChart: ChartData = {
			...chartData,
			id: chartId,
			timestamp: Math.round(Date.now() / 1000),
		};

		charts.push(savedChart);
		saveToStorage(storageKeys.lastChartId, chartId);
		isDirty = true;
		return chartId;
	},

	async getChartContent(chartId) {
		const chart = charts.find((item) => item.id === chartId);
		if (!chart) {
			throw new Error("Chart not found");
		}
		return chart.content;
	},

	async getAllStudyTemplates() {
		return studyTemplates.map<StudyTemplateMetaInfo>((template) => ({
			name: template.name,
		}));
	},

	async removeStudyTemplate(studyTemplateInfo) {
		studyTemplates = studyTemplates.filter(
			(template) => template.name !== studyTemplateInfo.name,
		);
		isDirty = true;
	},

	async saveStudyTemplate(studyTemplateData) {
		studyTemplates = studyTemplates.filter(
			(template) => template.name !== studyTemplateData.name,
		);
		studyTemplates.push(studyTemplateData);
		isDirty = true;
	},

	async getStudyTemplateContent(studyTemplateInfo) {
		const template = studyTemplates.find(
			(item) => item.name === studyTemplateInfo.name,
		);
		if (!template) {
			throw new Error("Study template not found");
		}
		return template.content;
	},

	async getDrawingTemplates(toolName) {
		return drawingTemplates
			.filter((template) => template.toolName === toolName)
			.map((template) => template.name);
	},

	async loadDrawingTemplate(toolName, templateName) {
		const template = drawingTemplates.find(
			(item) => item.toolName === toolName && item.name === templateName,
		);
		if (!template) {
			throw new Error("Drawing template not found");
		}
		return template.content;
	},

	async removeDrawingTemplate(toolName, templateName) {
		drawingTemplates = drawingTemplates.filter(
			(template) =>
				!(template.toolName === toolName && template.name === templateName),
		);
		isDirty = true;
	},

	async saveDrawingTemplate(toolName, templateName, content) {
		drawingTemplates = drawingTemplates.filter(
			(template) =>
				!(template.toolName === toolName && template.name === templateName),
		);
		drawingTemplates.push({ content, name: templateName, toolName });
		isDirty = true;
	},

	async getChartTemplateContent(templateName) {
		const template = chartTemplates.find((item) => item.name === templateName);
		if (!template) {
			throw new Error("Chart template not found");
		}
		return {
			content: structuredClone(template.content),
		} satisfies ChartTemplate;
	},

	async getAllChartTemplates() {
		return chartTemplates.map((template) => template.name);
	},

	async saveChartTemplate(newName, theme) {
		const existing = chartTemplates.find(
			(template) => template.name === newName,
		);
		if (existing) {
			existing.content = theme;
		} else {
			chartTemplates.push({ content: theme, name: newName });
		}
		isDirty = true;
	},

	async removeChartTemplate(templateName) {
		chartTemplates = chartTemplates.filter(
			(template) => template.name !== templateName,
		);
		isDirty = true;
	},

	async saveLineToolsAndGroups(layoutId, chartId, state) {
		if (!layoutId || !state.sources) {
			return;
		}

		const key = getDrawingKey(layoutId, chartId);
		if (!drawings[key]) {
			drawings[key] = {};
		}

		for (const [id, toolState] of state.sources.entries()) {
			if (toolState === null) {
				delete drawings[key][String(id)];
				continue;
			}

			drawings[key][String(id)] = toolState;
		}

		isDirty = true;
	},

	async loadLineToolsAndGroups(
		layoutId,
		chartId,
		_requestType: LineToolsAndGroupsLoadRequestType,
		_requestContext: LineToolsAndGroupsLoadRequestContext,
	) {
		if (!layoutId) {
			return null;
		}

		const key = getDrawingKey(layoutId, chartId);
		const raw = drawings[key];
		if (!raw) {
			return null;
		}

		const sources = new Map<EntityId, LineToolState | null>();

		for (const [id, state] of Object.entries(raw)) {
			sources.set(id as unknown as EntityId, state);
		}

		return {
			groups: new Map(),
			sources,
		} satisfies Partial<LineToolsAndGroupsState>;
	},
};
